import { cn } from "@app/lib/utils";
import { Link } from "@design-system/components";
import { headerLinks } from "@design-system/data";
import { trans } from "@mongez/localization";
import { useNavigate } from "@remix-run/react";
import { ChevronDown } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { isApple } from "../utils";

type HeaderLink = {
  title: string;
  href?: string;
  children?: HeaderLink[];
};

export function NavLinks() {
  return (
    <nav className="flex items-center gap-1">
      {headerLinks.map((link, index) => (
        <NavLinkItem key={index} link={link} />
      ))}
    </nav>
  );
}

function NavLinkItem({ link }: { link: HeaderLink }) {
  const [isOpen, setIsOpen] = useState(false);
  const [openChildIndex, setOpenChildIndex] = useState<number | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  const isMac = isApple();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    function handleEscapeKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  const openDropdown = () => {
    setIsOpen(true);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setOpenChildIndex(null);
  };

  if (link.children) {
    const children = link.children;

    return (
      <div
        ref={dropdownRef}
        className="relative"
        onMouseEnter={openDropdown}
        onMouseLeave={closeDropdown}
      >
        <button
          type="button"
          onClick={() => link.href && navigate(link.href)}
          className={cn(
            "flex items-center gap-1 px-3 py-2 text-lg hover:font-bold hover:text-tech-blue 2xl:text-xl",
            isOpen && "font-bold text-tech-blue",
            isMac ? "mac-font" : "not-italic",
          )}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          {trans(link.title)}
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        <div
          className={`absolute start-0 top-full z-50 min-w-[21.7rem] rounded-[1.5rem] bg-secondary p-3 ${
            isOpen
              ? "translate-y-0 opacity-100"
              : "pointer-events-none -translate-y-2 opacity-0"
          }`}
        >
          {children.map((child, index) => (
            <Fragment key={index}>
              <div
                className="relative"
                onMouseEnter={() =>
                  setOpenChildIndex(child.children ? index : null)
                }
                onFocus={() => setOpenChildIndex(child.children ? index : null)}
              >
                {child.children ? (
                  <>
                    <button
                      type="button"
                      className={cn(
                        "flex w-full items-center justify-between gap-4 p-4 text-start text-xl font-semibold text-white",
                        isMac ? "mac-font" : "not-italic",
                      )}
                      aria-expanded={openChildIndex === index}
                      aria-haspopup="true"
                    >
                      <span>{trans(child.title)}</span>
                      <ChevronDown className="h-4 w-4 -rotate-90 rtl:rotate-90" />
                    </button>
                    <div
                      className={cn(
                        "absolute start-full top-0 z-50 ms-2 min-w-[18rem] rounded-[1.5rem] bg-secondary p-3 transition-all duration-200",
                        openChildIndex === index
                          ? "translate-x-0 opacity-100"
                          : "pointer-events-none -translate-x-2 opacity-0 rtl:translate-x-2",
                      )}
                    >
                      {child.children.map((grandChild, grandChildIndex) => (
                        <Fragment key={grandChildIndex}>
                          <Link
                            to={grandChild.href as string}
                            viewTransition
                            className={cn(
                              "block p-4 text-lg font-semibold text-white",
                              isMac ? "mac-font" : "not-italic",
                            )}
                            onClick={closeDropdown}
                          >
                            {trans(grandChild.title)}
                          </Link>
                          {grandChildIndex !== child.children!.length - 1 && (
                            <hr className="border border-white border-opacity-10" />
                          )}
                        </Fragment>
                      ))}
                    </div>
                  </>
                ) : (
                  <Link
                    to={child.href as string}
                    viewTransition
                    className={cn(
                      "block p-4 text-xl font-semibold text-white",
                      isMac ? "mac-font" : "not-italic",
                    )}
                    onClick={closeDropdown}
                  >
                    {trans(child.title)}
                  </Link>
                )}
              </div>
              {index !== children.length - 1 && (
                <hr className="border border-white border-opacity-10" />
              )}
            </Fragment>
          ))}
        </div>
      </div>
    );
  }

  return (
    <Link
      to={link.href as string}
      className={cn(
        "px-3 py-2 text-xl hover:font-bold hover:text-tech-blue",
        isMac ? "mac-font" : "not-italic",
      )}
    >
      {trans(link.title)}
    </Link>
  );
}
